/* eslint-disable @wyz/module/no-relative-import */
import { FormatDate, Icon, Text } from '@wyz/ui';
import { Trans, useLocale } from '~/common/context';
import { CHANGE_SET_TYPE, CHANGE_TYPE } from '~/connect/schemas';
import { IChangeLog, IFormattedChangeLogField } from '~/connect/types';
import { ChangeLogFormatHelpers } from '~/connect/helpers';

const ACTION = {
  [CHANGE_TYPE.INSERT]: {
    variant: 'create' as const,
    i18nKey: 'app.changelog.insert' as const,
  },
  [CHANGE_TYPE.UPDATE]: {
    variant: 'update' as const,
    i18nKey: 'app.changelog.update' as const,
  },
  [CHANGE_TYPE.REPLACE]: {
    variant: 'update' as const,
    i18nKey: 'app.changelog.update' as const,
  },
};

function DiffValue({
  value,
  tone,
  isDate,
  locale,
}: {
  value: string;
  tone: 'old' | 'new';
  isDate: boolean;
  locale: string;
}) {
  if (value === '') {
    // Em-dash placeholder rendered via CSS (::after) to stay out of i18n scope.
    return <span className='cl-value cl-value--empty' aria-hidden='true' />;
  }
  if (isDate) {
    return (
      <span className={`cl-value cl-value--${tone}`}>
        <FormatDate date={new Date(Number(value))} locale={locale} />
      </span>
    );
  }
  return (
    <span
      className={`cl-value cl-value--${tone}${
        ChangeLogFormatHelpers.isTokenValue(value) ? ' cl-value--mono' : ''
      }`}
    >
      {value}
    </span>
  );
}

function ChangeTag({ type }: { type: 'added' | 'deleted' }) {
  return (
    <span className='cl-tag'>
      <span className={`cl-dot cl-dot--${type}`} aria-hidden='true' />
      <Trans
        i18nKey={
          type === CHANGE_SET_TYPE.ADDED
            ? 'app.changelog.added'
            : 'app.changelog.deleted'
        }
      />
    </span>
  );
}

function FieldRow({
  field,
  locale,
  hideTag,
}: {
  field: IFormattedChangeLogField;
  locale: string;
  hideTag: boolean;
}) {
  const hasOld = field.oldValue !== null;
  const hasNew = field.newValue !== null;
  const isModification = hasOld && hasNew;

  return (
    <div className='cl-field'>
      <div className='cl-field__label'>{field.label}</div>
      <div className='cl-field__diff'>
        {hasOld && (
          <DiffValue
            value={field.oldValue as string}
            tone='old'
            isDate={field.isDate}
            locale={locale}
          />
        )}
        {isModification && (
          <Icon
            icon='arrow-right'
            width={16}
            height={16}
            className='cl-arrow'
          />
        )}
        {hasNew && (
          <DiffValue
            value={field.newValue as string}
            tone='new'
            isDate={field.isDate}
            locale={locale}
          />
        )}
        {!hideTag &&
          (field.type === CHANGE_SET_TYPE.ADDED ||
            field.type === CHANGE_SET_TYPE.DELETED) && (
            <ChangeTag type={field.type} />
          )}
      </div>
    </div>
  );
}

export function ChangeLogEntry({ log }: { log: IChangeLog }) {
  const { locale } = useLocale();
  const action = ACTION[log.type];
  const author = ChangeLogFormatHelpers.extractUpdatedBy(log);
  const groups = ChangeLogFormatHelpers.buildChangeGroups(log);

  return (
    <div className={`cl-entry cl-entry--${action.variant}`}>
      <div className='cl-entry__doc'>
        {groups.map((group) => {
          // Whole entity added/removed → tag the group once, drop per-field tags.
          const groupTag =
            group.label &&
            (group.actionType === CHANGE_SET_TYPE.ADDED ||
              group.actionType === CHANGE_SET_TYPE.DELETED)
              ? group.actionType
              : null;
          return (
            <div className='cl-group' key={group.key}>
              {group.label && (
                <div className='cl-group__head'>
                  <span>{group.label}</span>
                  {groupTag && <ChangeTag type={groupTag} />}
                </div>
              )}
              <div className='cl-group__fields'>
                {group.fields.map((field) => (
                  <FieldRow
                    field={field}
                    locale={locale}
                    hideTag={groupTag !== null}
                    key={field.key}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className='cl-entry__who'>
        <Text
          heading='xs'
          weight='semi-bold'
          className={author ? undefined : 'text-muted'}
        >
          {author ?? '—'}
        </Text>
        <span className={`cl-action cl-action--${action.variant}`}>
          <span
            className={`cl-dot cl-dot--${action.variant}`}
            aria-hidden='true'
          />
          <Text heading='xxs' color='inherit'>
            <Trans i18nKey={action.i18nKey} />
          </Text>
        </span>
      </div>

      <div className='cl-entry__when'>
        <FormatDate date={new Date(log.loggedAt)} locale={locale} />
      </div>
    </div>
  );
}
